import { useEffect } from 'react';
import 'firebaseui/dist/firebaseui.css';
import type { Auth } from 'firebase/auth';
import type firebaseui from 'firebaseui';
import { ELEMENT_ID } from './constants.ts';
/**
 * This is a rewrite of the firebase/firebaseui-web-react auth button
 * so that it supports Next.js's requirement that no css be imported
 * in the node_modules directory. Instead, you should use this component
 * and make sure that you add this import to your page file:
 * > import "<path to>/node_modules/firebaseui/dist/firebaseui.css";
 *
 * This rewrite was done by Zack Sheppard (@zackdotcomputer) in Feb 2021.
 * It is relicensed under Google's Apache 2 License below.
 */

/**
 * Copyright 2017 Google Inc. All Rights Reserved.
 *
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// Promise that resolves unless the FirebaseUI instance is currently being deleted.
let firebaseUiDeletion = Promise.resolve();

type FirebaseAuthProps = {
    uiConfig: firebaseui.auth.Config;
    firebaseAuth: Auth;
    className?: string;
};

/**
 * React Component wrapper for the FirebaseUI Auth widget.
 */
export const FirebaseAuth = ({ uiConfig, firebaseAuth, className }: FirebaseAuthProps) => {
    useEffect(() => {
        let isUnmounted = false;
        let widget: firebaseui.auth.AuthUI | undefined;
        let unregisterAuthObserver: (() => void) | undefined;

        // Firebase UI only works on the client, so it's loaded inside the effect to keep SSR working.
        // Wait in case the firebase UI instance is being deleted, which happens on a quick unmount/remount.
        firebaseUiDeletion
            .then(() => import('firebaseui'))
            .then((firebaseui) => {
                if (isUnmounted) return;

                widget = firebaseui.auth.AuthUI.getInstance() || new firebaseui.auth.AuthUI(firebaseAuth);
                if (uiConfig.signInFlow === 'popup') {
                    widget.reset();
                }

                // Reset the widget if the user signs out while it's mounted.
                let userSignedIn = false;
                unregisterAuthObserver = firebaseAuth.onAuthStateChanged((user) => {
                    if (!user && userSignedIn) {
                        widget?.reset();
                    }
                    userSignedIn = !!user;
                });

                widget.start('#' + ELEMENT_ID, uiConfig);
            });

        return () => {
            isUnmounted = true;
            firebaseUiDeletion = firebaseUiDeletion.then(() => {
                unregisterAuthObserver?.();
                return widget?.delete();
            });
        };
        // Mount-only, like the original class component: callers pass inline uiConfig objects.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <div className={className} id={ELEMENT_ID} />;
};

export default FirebaseAuth;
